import { useTranslation } from "react-i18next";

export default function ArticleContent() {
  const { t } = useTranslation("blog-condens");

  const causes = t("sections.oorzaken.causes", { returnObjects: true }) as Array<{
    title: string;
    text: string;
  }>;

  const binnenkantOplossingen = t("sections.binnenkant.oplossingen", { returnObjects: true }) as Array<{
    title: string;
    text: string;
  }>;

  const professioneelSituaties = t("sections.professioneel.situations", { returnObjects: true }) as Array<{
    title: string;
    text: string;
  }>;

  return (
    <div className="flex flex-col gap-10 md:gap-14">
      {/* Wat veroorzaakt condens */}
      <section id="wat-veroorzaakt-condens">
        <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-3 md:mb-4">
          {t("sections.oorzaken.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3">
          {t("sections.oorzaken.p1")}
        </p>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3">
          {t("sections.oorzaken.p2")}
        </p>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
          {t("sections.oorzaken.p3")}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
          {causes.map((c, idx) => (
            <div key={idx} className="bg-brand-cream rounded-xl p-4 md:p-5 border border-brand-border">
              <h3 className="font-geist font-semibold text-sm md:text-base text-brand-text mb-1.5 md:mb-2">
                {c.title}
              </h3>
              <p className="font-geist text-sm text-brand-text/70 leading-relaxed">
                {c.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Binnenkant */}
      <section id="condens-binnenkant">
        <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-3 md:mb-4">
          {t("sections.binnenkant.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3">
          {t("sections.binnenkant.p1")}
        </p>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
          {t("sections.binnenkant.p2")}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
          {binnenkantOplossingen.map((o, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 md:p-5 border border-brand-border">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-brand-yellow flex items-center justify-center">
                  <i className="ri-check-line w-4 h-4 flex items-center justify-center text-sm text-brand-text"></i>
                </div>
                <h3 className="font-geist font-semibold text-sm md:text-base text-brand-text">
                  {o.title}
                </h3>
              </div>
              <p className="font-geist text-sm text-brand-text/70 leading-relaxed">
                {o.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Buitenkant */}
      <section id="condens-buitenkant" className="bg-brand-cream rounded-2xl p-5 md:p-8 border border-brand-border">
        <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-3 md:mb-4">
          {t("sections.buitenkant.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3">
          {t("sections.buitenkant.p1")}
        </p>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-4 md:mb-5">
          {t("sections.buitenkant.p2")}
        </p>
        <div className="bg-white rounded-xl p-4 md:p-5 border border-brand-border">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-brand-yellow flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line w-5 h-5 flex items-center justify-center text-lg text-brand-text"></i>
            </div>
            <p className="font-geist text-sm md:text-base text-brand-text/80 leading-relaxed">
              {t("sections.buitenkant.conclusion")}
            </p>
          </div>
        </div>
      </section>

      {/* Tussen glas */}
      <section id="condens-tussen-glas">
        <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-3 md:mb-4">
          {t("sections.tussenGlas.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3">
          {t("sections.tussenGlas.p1")}
        </p>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3">
          {t("sections.tussenGlas.p2")}
        </p>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
          {t("sections.tussenGlas.p3")}
        </p>
        <a
          href={t("sections.tussenGlas.cta.href")}
          className="inline-flex items-center gap-1.5 font-geist font-medium text-sm bg-brand-yellow text-brand-text px-5 py-2.5 rounded-full hover:bg-brand-yellow/90 transition-colors whitespace-nowrap"
        >
          {t("sections.tussenGlas.cta.text")}
          <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-sm"></i>
        </a>
      </section>

      {/* Professioneel ingrijpen */}
      <section id="professioneel-ingrijpen">
        <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-3 md:mb-4">
          {t("sections.professioneel.h2")}
        </h2>
        <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
          {t("sections.professioneel.p1")}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mb-5 md:mb-6">
          {professioneelSituaties.map((s, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 md:p-5 border border-brand-border">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-red-50 flex items-center justify-center">
                  <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center text-sm text-red-500"></i>
                </div>
                <h3 className="font-geist font-semibold text-sm md:text-base text-brand-text">
                  {s.title}
                </h3>
              </div>
              <p className="font-geist text-sm text-brand-text/70 leading-relaxed">
                {s.text}
              </p>
            </div>
          ))}
        </div>
        <a
          href={t("sections.professioneel.cta.href")}
          className="inline-flex items-center gap-1.5 font-geist font-medium text-sm bg-brand-text text-white px-5 py-2.5 rounded-full hover:bg-brand-text/90 transition-colors whitespace-nowrap"
        >
          {t("sections.professioneel.cta.text")}
          <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-sm"></i>
        </a>
      </section>
    </div>
  );
}